import LogCannon from '@ace-clublabs/log-cannon';
import { ReactElement, useEffect, useState } from 'react';
import { Cookies } from 'react-cookie';
import { createAceIdentity } from './utils/createAceIdentity';
import { LoadingView } from '../LoadingView/LoadingView';
import { AUTH_STORAGE_LABEL, Pages, AUTH_QUERY_PARAM_NAME, TOKEN_QUERY_PARAM } from '../../utils/constants';
import { useHistory, useLocation } from 'react-router-dom';
import { sources } from '../../api/sources';
import { mapMemberDetailsApiResponseToState } from '../../state/apiToStateMaps';
import { setGlobalState } from '../../state/state';
import { UtagHelper } from '../../utils/UtagHelper';
import StatusCode from 'status-code-enum';
import { IMemberDetails } from '../../state/model';
import {
  useSessionTimeout,
  Session,
  getPostLogoutRedirectUrl,
  REPLACEMENT_CARD_DEFAULT_SESSION_DURATION,
} from '../../hooks';
import { getIdentityUrl } from './utils/helpers';

const REDIRECT_PATH = `/membership/replacementcard`;
const QUERY_PARAM_KEY = 'query_params';
const PATH_KEY = 'path';

export interface IAuthenticationProviderProps {
  children: ReactElement;
  useStubs: boolean;
}

export const AuthenticationProvider = (props: IAuthenticationProviderProps) => {
  const [finishLoadingProps, setFinishLoadingProps] = useState({ finished: false, onFinish: () => {} });
  const [isAuthenticated, setAuthenticated] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);

  const { setSessionTimeoutParameters } = useSessionTimeout();
  const setToken = (token: string, isQueryToken = false) => {
    const cookie = new Cookies();
    cookie.set(AUTH_STORAGE_LABEL, token, { secure: true, path: '/' });
    if (isQueryToken) {
      cookie.set(AUTH_QUERY_PARAM_NAME, token, { secure: true, path: '/' });
    }
  };

  const reAddQueryParams = () => {
    const params = new URLSearchParams(window.location.search);
    const savedQueryParams = sessionStorage.getItem(QUERY_PARAM_KEY);
    sessionStorage.removeItem(QUERY_PARAM_KEY);
    if (savedQueryParams) {
      savedQueryParams.split('&').forEach((param) => {
        const [key, value] = param.split('=');
        params.set(key, value);
      });
      setQueryParams(params.toString());
    }
  };

  const finishAuth = (token: string, onFinishAuth: () => void) => {
    setToken(token);
    setAuthenticated(true);
    onFinishAuth();
  };

  const extractTokenFromQueryParams = (): [token: string | null, filteredQueryParams: URLSearchParams] => {
    const queryParams = new URLSearchParams(window.location.search);
    const queryToken = queryParams?.get(TOKEN_QUERY_PARAM);
    queryParams.delete(TOKEN_QUERY_PARAM);
    return [queryToken, queryParams];
  };

  const [tokenFromQueryParams, filteredQueryParams] = extractTokenFromQueryParams();

  if (tokenFromQueryParams) {
    setQueryParams(filteredQueryParams.toString());
  }

  const loadFromStubs = () => {
    setAuthenticated(true);
    const stubbedAuth = [
      'eyJraWQiOiJIcnlEM1plSXE5TjRJZGhUNWRuQjNrSHdsRE4yR2pjXzNsZFRxMF90cDZrIiwiYWxnIjoiUlMyNTYifQ.',
      'eyJ2ZXIiOjEsImp0aSI6IkFULm1RQnNGU3Zsal9NQmVBMkpwSWdjb2lMVjJfMGNUMlpva3Y2a0kxcWlwNHMub2FyMWViZ2Zua1ZGcnFCZ3owaDciLCJpc3MiOiJodHRwczovL2FjZS1pZGVudGl0eS5va3RhcHJldmlldy5jb20vb2F1dGgyL2F1c21vZnF1bzYyY0h0WU5rMGg3IiwiYXVkIjoiMG9hbW45NWNyNmZKODBZdFYwaDciLCJpYXQiOjE2Mzg5OTg1ODEsImV4cCI6MTYzOTAwMjE4MSwiY2lkIjoiMG9hbW45NWNyNmZKODBZdFYwaDciLCJ1aWQiOiIwMHUxMzA0Z2xrdTVqeVJnZzBoOCIsInNjcCI6WyJvZmZsaW5lX2FjY2VzcyIsIm9wZW5pZCIsImNsdWJDb2RlIiwic2NvcGUiLCJwcm9maWxlIiwiY3VzdG9tZXJJZCJdLCJzdWIiOiI5MDA4ODAzN0BhYWEtY2FsaWYuY29tIiwic2VsZlJlY292ZXJ5IjpmYWxzZSwiY2x1YkNvZGUiOiIwMDQiLCJzY29wZSI6WyJndWlkZXdpcmUuZWRnZS5wb2xpY3kuQ0FBMTQ3NTY2NTUwLkFMTCIsImd1aWRld2lyZS5lZGdlLnBvbGljeS5DSE8xNDc1NDEwMDIuQUxMIl0sImN1c3RvbWVySWQiOiI2NDI0NDYwMjAwMzEyNDIiLCJwcmVmZXJyZWRfdXNlcm5hbWUiOiI5MDA4ODAzN0BhYWEtY2FsaWYuY29tIn0.',
      'ZF1kVFLdR9I17dHWiMUg6BOE8Pz3NJQY9XFvmRK-jz51UyECkeCpa0ELAnlUqTv7AtAVMejd9ODBLmvmTDYHjpwUPpbsKj2g1g8TXPfuM2lcGcfnHS_qjzdhZTwHW0kTBB546otOjworl4OyPJJKlqX0rhLqnyF3upBkVEOhuA4Pr2Ad7VwZFN3gvI5t9SYrsRhnkIbp2PUuqNTvlfJAI0Ww6OIOTp1okZNNVrCfS2zV6NohL9GvA4pBwbYGPEiPzejETAwNnlGS1uimFZC4bt1l3OVKdvu2Jas-uQnp2tnJYE5G8p8jhPUkRmBwyFE1YOZmws_7N1WYNYzWu0_myw',
    ].join('');
    setToken(stubbedAuth);
  };

  async function authenticate() {
    if (props.useStubs) {
      loadFromStubs();
    } else {
      try {
        const aceIdentity = createAceIdentity(window.location.host, REDIRECT_PATH);
        await aceIdentity.ready();
        const tokens = await aceIdentity.tokens();
        const accessToken = tokens?.accessToken || '';
        if (accessToken) {
          const session = new Session(aceIdentity, REPLACEMENT_CARD_DEFAULT_SESSION_DURATION); // 5 minute session for testing
          const expirationTime = await session.getSessionExpirationTime();
          const postLogoutRedirectUrl = getPostLogoutRedirectUrl(Pages.TimeoutError);
          const logoutUrl = getIdentityUrl('logout', postLogoutRedirectUrl);

          setSessionTimeoutParameters({
            expiresAt: expirationTime,
            onSessionExpired: () => {
              if (isLoading) setFinishLoadingProps({ finished: true, onFinish: () => setIsLoading(false) });
              window.location.assign(logoutUrl);
            },
          });
          finishAuth(accessToken, loadMemberInfo);
        }
      } catch (error: any) {
        LogCannon.error('identity Error thrown getting token from PKCE', error);
        history.push(Pages.SorryPage);
      }
    }
  }

  function redirectBasedOnMemberDetails(memberDetails: IMemberDetails, history: ReturnType<typeof useHistory>) {
    if (memberDetails.members?.length === 1) {
      history.push(Pages.MembershipCard);
    } else {
      history.push(Pages.SelectMemberPage);
    }
  }

  const loadMemberInfo = async () => {
    try {
      // setLoadingMessage('Gathering your membership information');
      const memberDetails = await sources.getMembershipDetails();
      // @ts-ignore
      if (!memberDetails || memberDetails.error) {
        // @ts-ignore
        if (!memberDetails || memberDetails?.error?.code === StatusCode.ServerErrorServiceUnavailable) {
          setFinishLoadingProps({
            finished: true,
            onFinish: () => {
              setIsLoading(false);
              history.push(Pages.MantenancePage);
            },
          });
        } else {
          setFinishLoadingProps({
            finished: true,
            onFinish: () => {
              setIsLoading(false);
              history.push(Pages.SorryPage);
            },
          });
        }
      } else {
        const formattedMembershipDetails = mapMemberDetailsApiResponseToState(memberDetails);
        setGlobalState('memberDetails', formattedMembershipDetails);
        const baseTags = UtagHelper.buildTags(memberDetails);
        setGlobalState('tags', baseTags);
        setFinishLoadingProps({
          finished: true,
          onFinish: () => {
            setIsLoading(false);
            redirectBasedOnMemberDetails(memberDetails, history);
          },
        });
      }
    } catch (error) {
      console.error('Error loading member info:', error);
      setFinishLoadingProps({
        finished: true,
        onFinish: () => {
          setIsLoading(false);
          history.push(Pages.SorryPage);
        },
      });
    }
  };

  useEffect(() => {
    const isErrorOrMaintenancePage = [Pages.SorryPage, Pages.TimeoutError, Pages.MantenancePage].includes(
      location.pathname as Pages
    );

    if (isErrorOrMaintenancePage) {
      setFinishLoadingProps({
        finished: true,
        onFinish: () => {
          setIsLoading(false);
        },
      });
      return;
    }

    const path = window.location.pathname.replace(REDIRECT_PATH, '');
    if (path) {
      sessionStorage.setItem(PATH_KEY, path);
    }

    const queryParamsStr = extractTokenFromQueryParams()[1].toString();
    if (queryParamsStr) {
      sessionStorage.setItem(QUERY_PARAM_KEY, queryParamsStr);
    } else {
      reAddQueryParams();
    }

    if (!isAuthenticated) {
      authenticate();
    }
  }, []);

  return isLoading ? (
    <LoadingView finishProps={finishLoadingProps} message={'Logging in'} estimatedTimeInSeconds={25} />
  ) : (
    <>{props.children}</>
  );
};

const setQueryParams = (params: string) => {
  const currentUrl = window.location.href;
  const newUrl = currentUrl.split('?')[0] + `${params ? `?${params}` : ''}`;
  window.history.replaceState(null, '', newUrl);
};

const authenticateWithPKCE = async (finishAuth: (token: string) => void) => {
  const aceIdentity = createAceIdentity(window.location.host, REDIRECT_PATH);
  await aceIdentity.ready();

  const tokens = await aceIdentity.tokens();
  const accessToken = tokens?.accessToken || '';
  if (accessToken) {
    finishAuth(accessToken);
  }
};
